"use client"

import {
    Card,
    CardContent,
    CardTitle,
} from "@/components/ui/card";
import {Input} from "@/components/ui/input";
import {Button} from "@/components/ui/button";

import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import { z } from "@/lib/zod";
import React, {useState} from "react";
import {IGuideItem} from "@/types/global";
import {env} from "next-runtime-env";
import {trackLead} from "@/lib/analytics";
import {Loader} from "lucide-react";
import {CONTACT_FALLBACK, GUIDE_TOO_MANY_MESSAGE, messageForFailedResponse, NETWORK_ERROR_MESSAGE} from "@/lib/form-errors";

const formSchema = z.object({
    guide_type: z.string().trim(),
    first_name: z.string().trim().min(1, '*Required'),
    last_name: z.string().trim().min(1, '*Required'),
    email: z.string().trim().email("This is not a valid email"),
    // Honeypot: hidden from people, so only bots fill it in. The API treats a
    // filled value as spam and sends nothing.
    website: z.string().optional(),
})

type inferGuidType = z.infer<typeof formSchema>;

export default function Guides({guideItem}: IGuideItem) {

    const apiUri = env('NEXT_PUBLIC_API_URI');
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [buttonIsLoading, setButtonIsLoading] = useState<boolean | undefined>()

    const methods = useForm<inferGuidType>({
        resolver: zodResolver(formSchema),
        defaultValues:
            {
                guide_type: guideItem.guideType,
                first_name: '',
                last_name: '',
                email: '',
                website: '',
            },
        reValidateMode: "onBlur",
        mode: "onBlur",
    });

    const {
        register,
        handleSubmit,
        formState: {},
        control,
        reset,
    } = methods;

    async function onSubmit(data: inferGuidType){
        setButtonIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        try {
            const res = await fetch(`${apiUri}/api/v1/leads/guides/send?guide_type=${encodeURIComponent(data.guide_type)}`, {
                method: 'POST',
                body: JSON.stringify(data),
                headers: {'Content-Type': 'application/json',},
            });
            // The API used to answer 201 with `false` when the email failed.
            const sent = res.ok && (await res.json().catch(() => true)) !== false;
            if (!sent) {
                setErrorMessage(res.ok
                    ? `We couldn't send the guide right now. ${CONTACT_FALLBACK}`
                    : res.status === 429
                        ? GUIDE_TOO_MANY_MESSAGE
                        : await messageForFailedResponse(res));
                return;
            }
            trackLead("guide_request");
            setSuccessMessage(`${guideItem.title} is on the way - check your email.`)
            reset()

            // Set a timer to hide the success message after 10 seconds
            setTimeout(() => {
                setSuccessMessage("")
            }, 10000); // 10000 ms = 10 seconds
        } catch {
            setErrorMessage(NETWORK_ERROR_MESSAGE);
        } finally {
            setButtonIsLoading(false);
        }
    }

    return (
        <Form {...methods} >
            <form onSubmit={handleSubmit(onSubmit)} className="w-full md:max-w-[800px]">
                <Card className="rounded shadow-sm bg-white">
                    <CardTitle className="bg-gray-300 rounded-t p-3">
                        <div className="flex w-full">
                            <span className="w-full">{guideItem.title}</span>

                                <span role="status" className="text-right justify-end w-full font-sans font-light text-green-500 text-base">
                                        {successMessage}
                                    </span>

                        </div>
                    </CardTitle>
                    <CardContent className="flex flex-col grow md:flex-row flex-wrap gap-5 pt-5">
                        <FormField
                            control={control}
                            name="guide_type"
                            render={() => (
                                <FormItem hidden={true}>
                                    <FormControl>
                                        <Input {...register("guide_type")} ></Input>
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="first_name"
                            render={() => (
                                <FormItem className="grow font-thin">
                                    <FormControl>
                                        <Input {...register("first_name")} placeholder="First Name"></Input>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="last_name"
                            render={() => (
                                <FormItem className="grow font-thin">
                                    <FormControl>
                                        <Input {...register("last_name")} placeholder="Last Name"></Input>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="email"
                            render={() => (
                                <FormItem className="grow font-thin">
                                    <FormControl>
                                        <Input {...register("email")} placeholder="Email"></Input>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        {/* Honeypot: off-screen and inert, so people and screen readers never reach it. */}
                        <div inert aria-hidden="true" className="absolute -left-[10000px] h-0 w-0 overflow-hidden">
                            <label htmlFor={`website-${guideItem.guideType}`}>Leave this field empty</label>
                            <input id={`website-${guideItem.guideType}`} type="text" tabIndex={-1}
                                   autoComplete="off" {...register("website")} />
                        </div>
                        <Button type="submit" variant="brand" disabled={buttonIsLoading}>
                            {buttonIsLoading ? (
                                <Loader className="animate-spin"/> // Spinner icon
                            ) : (
                                "Get The Guide!"
                            )}
                        </Button>
                        {errorMessage && (
                            <p role="alert" className="w-full text-sm text-red-600">{errorMessage}</p>
                        )}
                    </CardContent>
                </Card>
            </form>
        </Form>
    );
}
